/**
 * WorkItemPreviewDrawer — Preview rápido para modos Kanban e Timeline.
 * Não abre execução; oferece atalho explícito para o modo Lista.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, X } from 'lucide-react';
import type { UnifiedWorkItem } from '../types/workItem';
import {
  WORK_SOURCE_COLORS,
  WORK_SOURCE_LABELS,
  WORK_STATUS_COLORS,
  WORK_STATUS_LABELS,
} from '../types/workItem';

interface WorkItemPreviewDrawerProps {
  item: UnifiedWorkItem | null;
  onClose: () => void;
  onOpenExecution: () => void;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export const WorkItemPreviewDrawer: React.FC<WorkItemPreviewDrawerProps> = ({
  item,
  onClose,
  onOpenExecution,
}) => {
  useEffect(() => {
    if (!item) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [item, onClose]);

  if (!item) return null;

  const stageName = item.source_context?.stage_name;
  const slaDeadline = item.source_context?.sla_deadline ?? item.due_date;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Preview do item"
        className="fixed inset-y-0 right-0 z-50 flex w-[min(420px,94vw)] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-[var(--border-default)] dark:bg-[var(--bg-elevated)]"
      >
        <header className="shrink-0 border-b border-slate-200 px-4 py-3 dark:border-[var(--border-default)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">Preview</p>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${WORK_SOURCE_COLORS[item.source]}`}>
                  {WORK_SOURCE_LABELS[item.source]}
                </span>
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-medium ${WORK_STATUS_COLORS[item.status]}`}>
                  {WORK_STATUS_LABELS[item.status]}
                </span>
                {item.is_overdue && (
                  <span className="rounded bg-red-100 px-1.5 py-0.5 text-[9px] font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">
                    ATRASADO
                  </span>
                )}
              </div>
              <h2 className="mt-2 text-[14px] font-bold leading-snug text-slate-900 dark:text-[var(--text-primary)]">
                {item.title}
              </h2>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-[var(--bg-surface)]"
              aria-label="Fechar"
            >
              <X size={16} />
            </button>
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {item.description && (
            <p className="mb-3 text-[11px] leading-relaxed text-slate-600 dark:text-[var(--text-secondary)]">
              {item.description}
            </p>
          )}

          <dl className="divide-y divide-slate-100 dark:divide-[var(--border-default)]">
            {item.assigned_to_name && (
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 py-2">
                <dt className="text-[9px] font-bold uppercase text-slate-400">Responsável</dt>
                <dd className="text-right text-[11px] font-semibold text-slate-800 dark:text-slate-200">{item.assigned_to_name}</dd>
              </div>
            )}
            {stageName && (
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 py-2">
                <dt className="text-[9px] font-bold uppercase text-slate-400">Etapa</dt>
                <dd className="text-right text-[11px] font-semibold text-slate-800 dark:text-slate-200">{stageName}</dd>
              </div>
            )}
            {item.priority && (
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 py-2">
                <dt className="text-[9px] font-bold uppercase text-slate-400">Prioridade</dt>
                <dd className="text-right text-[11px] font-semibold uppercase text-slate-800 dark:text-slate-200">{item.priority}</dd>
              </div>
            )}
            <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 py-2">
              <dt className="text-[9px] font-bold uppercase text-slate-400">Abertura</dt>
              <dd className="text-right font-mono text-[10px] tabular-nums text-slate-600 dark:text-slate-400">{formatDate(item.created_at)}</dd>
            </div>
            {slaDeadline && (
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 py-2">
                <dt className="text-[9px] font-bold uppercase text-slate-400">SLA / Prazo</dt>
                <dd className={`text-right font-mono text-[10px] tabular-nums ${item.is_overdue ? 'font-bold text-red-600' : 'text-slate-600 dark:text-slate-400'}`}>
                  {formatDate(slaDeadline)}
                </dd>
              </div>
            )}
            {item.source_context?.instance_title && (
              <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 py-2">
                <dt className="text-[9px] font-bold uppercase text-slate-400">Processo</dt>
                <dd className="truncate text-right text-[11px] font-semibold text-slate-800 dark:text-slate-200">{item.source_context.instance_title}</dd>
              </div>
            )}
          </dl>

          <p className="mt-4 rounded-sm border border-slate-100 bg-slate-50 px-3 py-2 text-[10px] leading-relaxed text-slate-500 dark:border-[var(--border-default)] dark:bg-[var(--bg-surface)]">
            Este preview é somente leitura. Para executar a tarefa, abra no modo Lista.
          </p>
        </div>

        <footer className="flex shrink-0 gap-2 border-t border-slate-200 px-4 py-3 dark:border-[var(--border-default)]">
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 flex-1 items-center justify-center rounded-sm border border-slate-200 text-[11px] font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-[var(--border-default)] dark:text-slate-300 dark:hover:bg-[var(--bg-surface)]"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={onOpenExecution}
            className="flex h-9 flex-[1.4] items-center justify-center gap-1.5 rounded-sm bg-blue-600 text-[11px] font-bold text-white transition hover:bg-blue-700"
          >
            Abrir Execução
            <ArrowRight size={14} />
          </button>
        </footer>
      </aside>
    </>,
    document.body,
  );
};

export default WorkItemPreviewDrawer;
